import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, DollarSign, TrendingDown, TrendingUp, RefreshCw, Calendar, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export default function DebtRecovery({ userInfo }) {
  // State
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Filters
  const [selectedCarrierFilters, setSelectedCarrierFilters] = useState([]);
  const [carrierFilterOpen, setCarrierFilterOpen] = useState(false);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  // Toggle modal
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [debtToToggle, setDebtToToggle] = useState(null);

  // Fetch debts
  const fetchDebts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/myDebt',
        {
          params: {
            agent_id: userInfo.agent_id
          }
        }
      );
      setDebts(response.data || []);
    } catch (err) {
      console.error('Error fetching debts:', err);
      toast.error('Failed to load debts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.agent_id) {
      fetchDebts();
    }
  }, [userInfo]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  // Get unique carriers
  const uniqueCarriers = useMemo(() => {
    const carrierMap = new Map();
    debts.forEach(debt => {
      if (debt.carrier_name) {
        carrierMap.set(debt.carrier_name, debt.carrier_name);
      }
    });
    return Array.from(carrierMap.values()).map(name => ({ id: name, name }));
  }, [debts]);

  // Filter debts
  const filteredDebts = useMemo(() => {
    let filtered = [...debts];

    // Filter by carriers
    if (selectedCarrierFilters.length > 0) {
      filtered = filtered.filter(debt => selectedCarrierFilters.includes(debt.carrier_name));
    }

    // Filter by status
    if (selectedStatusFilters.length > 0) {
      filtered = filtered.filter(debt => {
        if (selectedStatusFilters.includes('resolved') && debt.isResolved) return true;
        if (selectedStatusFilters.includes('unresolved') && !debt.isResolved) return true;
        return false;
      });
    }

    // Filter by date range
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(debt => {
        if (!debt.created_at) return false;
        const debtDate = new Date(debt.created_at);
        
        const fromDate = dateRange.from ? new Date(dateRange.from.setHours(0, 0, 0, 0)) : null;
        const toDate = dateRange.to ? new Date(dateRange.to.setHours(23, 59, 59, 999)) : null;
        
        if (fromDate && debtDate < fromDate) return false;
        if (toDate && debtDate > toDate) return false;
        return true;
      });
    }

    return filtered;
  }, [debts, selectedCarrierFilters, selectedStatusFilters, dateRange]);

  // Calculate dashboard stats based on filtered debts
  const dashboardStats = useMemo(() => {
    const totalDebts = filteredDebts.length;
    const totalAmount = filteredDebts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
    const resolvedDebts = filteredDebts.filter(d => d.isResolved).length;
    const resolvedAmount = filteredDebts.filter(d => d.isResolved).reduce((sum, debt) => sum + (debt.amount || 0), 0);
    const unresolvedDebts = filteredDebts.filter(d => !d.isResolved).length;
    const unresolvedAmount = filteredDebts.filter(d => !d.isResolved).reduce((sum, debt) => sum + (debt.amount || 0), 0);
    
    return {
      totalDebts,
      totalAmount,
      resolvedDebts,
      resolvedAmount,
      unresolvedDebts,
      unresolvedAmount
    };
  }, [filteredDebts]);

  // Handle toggle resolved status
  const handleToggleClick = (debt) => {
    setDebtToToggle(debt);
    setShowToggleModal(true);
  };

  // Confirm toggle
  const handleConfirmToggle = async () => {
    try {
      setSaving(true);
      
      // Call API to update resolved status
      await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/mydebt/resolved',
        {
          debt_id: debtToToggle.id
        }
      );
      
      // Update locally
      setDebts(prev => prev.map(debt => 
        debt.id === debtToToggle.id 
          ? { ...debt, isResolved: !debt.isResolved }
          : debt
      ));
      
      toast.success(`Debt marked as ${!debtToToggle.isResolved ? 'resolved' : 'unresolved'}`);
      setShowToggleModal(false);
      setDebtToToggle(null);
    } catch (err) {
      console.error('Error toggling debt status:', err);
      toast.error(err.response?.data?.message || 'Failed to update debt status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader>
          {/* Title and Refresh */}
          <div className="flex items-center justify-between mb-6">
            <CardTitle className="text-2xl font-bold text-neutral-800">Debt Recovery</CardTitle>
            <Button
              onClick={fetchDebts}
              variant="outline"
              size="sm"
              className="rounded-lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Dashboard Stats */}
          <div className="grid grid-cols-3 gap-4">
            {/* Total Debts */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">Total Debts</p>
                  <DollarSign className="w-5 h-5 text-[#b8941e]" />
                </div>
                <p className="text-4xl font-black text-[#b8941e] mb-1">{dashboardStats.totalDebts}</p>
                <p className="text-sm font-bold text-neutral-600">
                  ${dashboardStats.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
            </div>

            {/* Resolved */}
            <div className="relative overflow-hidden bg-gradient-to-br from-green-400 via-green-300 to-green-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Resolved</p>
                  <TrendingUp className="w-5 h-5 text-green-700" />
                </div>
                <p className="text-4xl font-black text-green-900 mb-1">{dashboardStats.resolvedDebts}</p>
                <p className="text-sm font-bold text-green-700">
                  ${dashboardStats.resolvedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/30 rounded-full blur-2xl"></div>
            </div>

            {/* Unresolved */}
            <div className="relative overflow-hidden bg-gradient-to-br from-red-400 via-red-300 to-red-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-red-900 uppercase tracking-wider">Unresolved</p>
                  <TrendingDown className="w-5 h-5 text-red-700" />
                </div>
                <p className="text-4xl font-black text-red-900 mb-1">{dashboardStats.unresolvedDebts}</p>
                <p className="text-sm font-bold text-red-700">
                  ${dashboardStats.unresolvedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/30 rounded-full blur-2xl"></div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mt-6">
            {/* Filter by Carrier */}
            <div className="flex-1">
              <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Carrier</label>
              <Popover open={carrierFilterOpen} onOpenChange={setCarrierFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={carrierFilterOpen}
                    className="w-full justify-between rounded-xl"
                  >
                    {selectedCarrierFilters.length === 0
                      ? "All Carriers"
                      : `${selectedCarrierFilters.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search carrier..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                      {uniqueCarriers.map((carrier) => (
                        <div
                          key={carrier.id}
                          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          onClick={() => {
                            setSelectedCarrierFilters(prev =>
                              prev.includes(carrier.name)
                                ? prev.filter(n => n !== carrier.name)
                                : [...prev, carrier.name]
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedCarrierFilters.includes(carrier.name)}
                            className="mr-2"
                          />
                          {carrier.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Filter by Status */}
            <div className="flex-1">
              <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Status</label>
              <Popover open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={statusFilterOpen}
                    className="w-full justify-between rounded-xl"
                  >
                    {selectedStatusFilters.length === 0
                      ? "All Status"
                      : `${selectedStatusFilters.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col p-1">
                    <div
                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                      onClick={() => {
                        setSelectedStatusFilters(prev =>
                          prev.includes('resolved')
                            ? prev.filter(s => s !== 'resolved')
                            : [...prev, 'resolved']
                        );
                      }}
                    >
                      <Checkbox
                        checked={selectedStatusFilters.includes('resolved')}
                        className="mr-2"
                      />
                      Resolved
                    </div>
                    <div
                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                      onClick={() => {
                        setSelectedStatusFilters(prev =>
                          prev.includes('unresolved')
                            ? prev.filter(s => s !== 'unresolved')
                            : [...prev, 'unresolved']
                        );
                      }}
                    >
                      <Checkbox
                        checked={selectedStatusFilters.includes('unresolved')}
                        className="mr-2"
                      />
                      Unresolved
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Range Filter */}
            <div className="flex-1">
              <label className="text-xs text-neutral-500 font-medium mb-1 block">Statement Date Range</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal rounded-xl",
                      !dateRange.from && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "MMM dd, yyyy")} -{" "}
                          {format(dateRange.to, "MMM dd, yyyy")}
                        </>
                      ) : (
                        format(dateRange.from, "MMM dd, yyyy")
                      )
                    ) : (
                      <span>Pick a date range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => setDateRange({ from: range?.from, to: range?.to })}
                    numberOfMonths={2}
                  />
                  {(dateRange.from || dateRange.to) && (
                    <div className="p-3 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDateRange({ from: null, to: null })}
                        className="w-full"
                      >
                        Clear Date Range
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Clear All Filters */}
          {(selectedCarrierFilters.length > 0 || selectedStatusFilters.length > 0 || dateRange.from || dateRange.to) && (
            <div className="flex items-center gap-2 mt-3">
              {selectedCarrierFilters.length > 0 && <Badge variant="outline" className="text-xs">{selectedCarrierFilters.length} carrier(s)</Badge>}
              {selectedStatusFilters.length > 0 && <Badge variant="outline" className="text-xs">{selectedStatusFilters.length} status</Badge>}
              {(dateRange.from || dateRange.to) && <Badge variant="outline" className="text-xs">Date range</Badge>}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedCarrierFilters([]);
                  setSelectedStatusFilters([]);
                  setDateRange({ from: null, to: null });
                }}
                className="h-6 text-xs"
              >
                Clear All
              </Button>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-6">
          {/* Debts Table */}
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto rounded-xl border border-neutral-100">
              {filteredDebts.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                  <p className="text-neutral-600">No debt records found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 first:rounded-tl-xl">Statement Date</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Carrier</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Amount</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Submitted By</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Status</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 last:rounded-tr-xl">Mark Resolved</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {filteredDebts.map((debt) => (
                      <tr key={debt.id} className="hover:bg-neutral-50 transition-all duration-200">
                        <td className="px-4 py-3">
                          <p className="text-sm text-neutral-800">{formatDate(debt.created_at)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="border-[#d4af37] text-[#d4af37] bg-[#fffef0]">
                            {debt.carrier_name || 'N/A'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-red-600">
                            ${debt.amount ? debt.amount.toFixed(2) : '0.00'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-neutral-600">{debt.submittedBy_name || 'N/A'}</p>
                        </td>
                        <td className="px-4 py-3">
                          {debt.isResolved ? (
                            <Badge className="bg-green-500 text-white text-xs">
                              Resolved
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500 text-white text-xs">
                              Unresolved
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <Switch
                              checked={debt.isResolved}
                              onCheckedChange={() => handleToggleClick(debt)}
                              disabled={debt.isResolved}
                              className={debt.isResolved ? "opacity-50 cursor-not-allowed" : ""}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Toggle Confirmation Modal */}
      <Dialog open={showToggleModal} onOpenChange={setShowToggleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this debt as {debtToToggle?.isResolved ? 'unresolved' : 'resolved'}?
            </DialogDescription>
          </DialogHeader>

          {debtToToggle && (
            <div className="space-y-3 py-4">
              <div className="bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Carrier:</span>
                    <span className="font-medium text-neutral-800">{debtToToggle.carrier_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Amount:</span>
                    <span className="font-semibold text-red-600">${debtToToggle.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Current Status:</span>
                    {debtToToggle.isResolved ? (
                      <Badge className="bg-green-500 text-white text-xs">Resolved</Badge>
                    ) : (
                      <Badge className="bg-red-500 text-white text-xs">Unresolved</Badge>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">New Status:</span>
                    {!debtToToggle.isResolved ? (
                      <Badge className="bg-green-500 text-white text-xs">Resolved</Badge>
                    ) : (
                      <Badge className="bg-red-500 text-white text-xs">Unresolved</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowToggleModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmToggle}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
